import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { AdminAuthService } from '../services/admin-auth.service';

const isAdminApiRequest = (url: string): boolean => /\/(api\/)?admin\//.test(url);
const isAdminLoginRequest = (url: string): boolean => /\/(api\/)?admin\/auth\/login\b/.test(url);
const isAdminRefreshRequest = (url: string): boolean => /\/(api\/)?admin\/auth\/refresh\b/.test(url);
const isAdminLogoutRequest = (url: string): boolean => /\/(api\/)?admin\/auth\/logout\b/.test(url);
const isAdminMeRequest = (url: string): boolean => /\/(api\/)?admin\/auth\/me\b/.test(url);
const RETRY_HEADER = 'X-Admin-Auth-Retry';

const buildLoginParams = (reason: 'expired' | 'forbidden', message?: string): Record<string, string> => {
  const params: Record<string, string> = { reason };
  const normalized = (message ?? '').trim();
  if (normalized) params['message'] = normalized;
  return params;
};

export const adminAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AdminAuthService);
  const router = inject(Router);

  const token = auth.token;
  const withCredentials = isAdminApiRequest(req.url)
    ? req.clone({
        withCredentials: true,
        ...(token ? { setHeaders: { Authorization: `Bearer ${token}` } } : {}),
      })
    : req;
  const withAuth = withCredentials;

  return next(withAuth).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse) || !isAdminApiRequest(withAuth.url)) {
        return throwError(() => err);
      }

      if (err.status === 401 && !isAdminLoginRequest(withAuth.url) && !isAdminRefreshRequest(withAuth.url) && !isAdminMeRequest(withAuth.url)) {
        if (withAuth.headers.get(RETRY_HEADER) === '1') {
          auth.logout(false);
          router.navigate(['/admin/login'], {
            queryParams: buildLoginParams('expired', auth.getApiErrorMessage(err, 'Tu sesión ha expirado')),
          });
          return throwError(() => err);
        }

        return auth.refreshAccessToken().pipe(
          switchMap(ok => {
            if (!ok) {
              auth.logout(false);
              router.navigate(['/admin/login'], {
                queryParams: buildLoginParams('expired', auth.getApiErrorMessage(err, 'Tu sesión ha expirado')),
              });
              return throwError(() => err);
            }

            // Reconstruir la petición con el token actualizado (no clonar la petición
            // original que puede tener el token expirado en el header Authorization)
            const newToken = auth.token;
            const retried = req.clone({
              withCredentials: true,
              headers: (newToken
                ? req.headers.set('Authorization', `Bearer ${newToken}`)
                : req.headers
              ).set(RETRY_HEADER, '1'),
            });
            return next(retried);
          }),
        );
      }

      if (err.status === 403) {
        const fallback = auth.getDefaultAdminUrl();
        if (fallback) {
          router.navigateByUrl(fallback);
        } else {
          auth.logout(false);
          router.navigate(['/admin/login'], {
            queryParams: buildLoginParams('forbidden', auth.getApiErrorMessage(err, 'No tienes permisos para entrar')),
          });
        }
      }

      return throwError(() => err);
    }),
  );
};
